/**
 * Pure mapping from a getCrmIngestionFields query result to grid custom-field
 * definitions. Extracted from use-company-custom-fields / use-contact-custom-fields
 * (which differed only by objectType) so the idle prefetch (use-idle-prefetch.ts)
 * can compute the exact same custom-column set the pages will build — the
 * prefetched gridSummary `columns` variable must match the page's byte-for-byte.
 */

import {
  GetCrmIngestionFieldsQuery,
  CrmIngestionObjectType,
} from '@/lib/graphql/types-and-hooks';
import {
  columnTypeFromCustomFieldType,
  columnTypeForStandardField,
  aiPromptFromFieldConfig,
  formulaFromFieldConfig,
  formulaResultTypeFromFieldConfig,
} from '@/lib/utils/custom-column';

export interface CustomFieldDefinition {
  id: string; // Backend field ID
  key: string; // e.g., "custom_field_name"
  label: string; // Display name
  type: string; // "text", "number", "date", etc.
  crmName: string; // Backend field name
  selectOptions?: string[]; // For single-select and multi-select types
  aiPrompt?: string; // AI_SEARCH fields: research prompt, parsed from fieldConfig
  formula?: string; // FORMULA fields: expression, parsed from fieldConfig
  formulaResultType?: string; // FORMULA fields: "number" | "string" | "date"
  fieldConfig?: string | null; // Raw backend fieldConfig, so edits can merge into it
}

/**
 * Grid-facing custom fields for one object type: non-standard, available-in-grid
 * fields, in the order the backend returns them (which fixes the column order the
 * grids append them in).
 */
export function customFieldsFromCrmIngestion(
  data: GetCrmIngestionFieldsQuery | undefined | null,
  objectType: CrmIngestionObjectType,
): CustomFieldDefinition[] {
  if (!data?.crmIngestionFields?.edges) return [];
  return data.crmIngestionFields.edges
    .map(edge => edge?.node)
    .filter((field): field is NonNullable<typeof field> => {
      return (
        field != null &&
        field.objectType === objectType &&
        field.availableInGrid === true &&
        !field.isStandardField // Only custom fields
      );
    })
    .map(field => {
      const customFieldType = field.customFieldType?.toLowerCase() || 'text';

      // Parse enumerationData for select options (single-select and multi-select)
      let selectOptions: string[] | undefined;
      // Note: customFieldType from backend is SINGLE_SELECT (with underscore), lowercase becomes single_select
      if (field.isEnumeration && field.enumerationData && (customFieldType === 'single_select' || customFieldType === 'multi_select')) {
        try {
          const parsed = JSON.parse(field.enumerationData as string);
          // enumerationData format: {options: [{label: "O1", value: "O1", active: true, ...}, ...]}
          selectOptions = (parsed?.options || []).map((opt: any) => opt.label || opt.value);
        } catch (e) {
          console.error('Failed to parse enumerationData for', field.crmName, e);
        }
      }

      return {
        id: field.id,
        key: `custom_${field.crmName}`,
        label: field.crmLabel,
        type: columnTypeFromCustomFieldType(field.customFieldType), // Map backend enum → valid UI ColumnType
        crmName: field.crmName,
        selectOptions,
        aiPrompt: aiPromptFromFieldConfig(field.fieldConfig),
        formula: formulaFromFieldConfig(field.fieldConfig),
        formulaResultType: formulaResultTypeFromFieldConfig(field.fieldConfig),
        fieldConfig: field.fieldConfig,
      };
    });
}

/**
 * The *standard* fields the backend tracks for one object type.
 *
 * These live in the same `crm_ingestion_field` table as custom fields (flagged
 * `isStandardField`), which is what makes them safe to reference from a formula:
 * the backend validates every reference against that table, so any field listed
 * here is guaranteed to resolve. `crmName` is the key that goes into the
 * expression, matching the `properties.<crmName>` the value is stored under.
 *
 * `columnTypeForStandardField` handles the typing: standard rows usually carry the
 * `custom_field_type` column default ('STRING'), so typing them the plain custom-field
 * way makes every standard field look like text and empties the formula builder's
 * number/date pickers.
 */
export function standardFieldsFromCrmIngestion(
  data: GetCrmIngestionFieldsQuery | undefined | null,
  objectType: CrmIngestionObjectType,
): CustomFieldDefinition[] {
  if (!data?.crmIngestionFields?.edges) return [];
  return data.crmIngestionFields.edges
    .map(edge => edge?.node)
    .filter((field): field is NonNullable<typeof field> => (
      field != null &&
      field.objectType === objectType &&
      field.isStandardField === true &&
      !!field.crmName
    ))
    .map(field => ({
      id: field.id,
      key: field.crmName,
      label: field.crmLabel || field.crmName,
      type: columnTypeForStandardField(field),
      crmName: field.crmName,
      fieldConfig: field.fieldConfig,
    }));
}
